import { CheckCircle2, Circle } from "lucide-react";
import { theme, type as text } from "../theme";
import { fmtDateTime } from "../lib/format";
import { RulerProgress } from "./Drawing";

// The history and the completeness checklist of one regulatory submission. Both come from the API
// (lib/regulatorySubmissions.ts submissionTimeline / submissionCompleteness); the checklist is
// re-evaluated against the form as it is filled in, using the structure the API sent.

export interface TimelineEvent { at: string; kind: string; label: string; detail: string | null }

export interface CompletenessSection {
  label: string; required: number; filled: number;
  fields: Array<{ key: string; label: string; required: boolean; filled: boolean }>;
}

const DOT: Record<string, string> = {
  created: theme.color.faint,
  ready: theme.color.accent,
  draft: theme.color.faint,
  submitted: theme.color.success,
  acknowledged: theme.color.success,
  withdrawn: theme.color.danger,
  share_created: theme.color.accent,
  share_viewed: theme.color.accent,
  share_revoked: theme.color.warning,
  share_expired: theme.color.warning,
};

export function SubmissionTimeline({ events }: { events: TimelineEvent[] }) {
  if (events.length === 0) return null;
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0 }}>
      {events.map((e, i) => (
        <li key={`${e.kind}-${e.at}-${i}`} style={{ display: "flex", gap: 10 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: DOT[e.kind] ?? theme.color.faint, marginTop: 4 }} />
            {i < events.length - 1 && <span style={{ flex: 1, width: 2, background: theme.color.border, minHeight: 18 }} />}
          </div>
          <div style={{ paddingBottom: 12, minWidth: 0 }}>
            <div style={text.itemTitle}>{e.label}</div>
            <div style={text.meta}>{fmtDateTime(e.at)}</div>
            {e.detail && <div style={{ ...text.hint, marginTop: 2 }}>{e.detail}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

/** Is a form value answered? Blank text is not; "false" (a No) is. */
const answered = (v: string | undefined) => (v ?? "").trim() !== "";

/** Required fields answered against the live form, from the sections the API returned. */
export function liveCompleteness(sections: CompletenessSection[], payload: Record<string, string>) {
  let required = 0;
  let filled = 0;
  const missing: string[] = [];
  for (const s of sections) {
    for (const f of s.fields) {
      if (!f.required) continue;
      required++;
      if (answered(payload[f.key])) filled++;
      else missing.push(f.label);
    }
  }
  return { required, filled, missing, percent: required === 0 ? 100 : Math.floor((filled / required) * 100) };
}

export function SubmissionChecklist({ sections, payload }: { sections: CompletenessSection[]; payload: Record<string, string> }) {
  if (sections.length === 0) {
    return <div style={{ ...text.hint, color: theme.color.faint }}>This kind of submission has no set form; attach what the authority asked for.</div>;
  }
  const live = liveCompleteness(sections, payload);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <RulerProgress value={live.percent} label="Required fields answered" style={{ flex: 1 }} />
        <span style={{ ...text.hint, fontWeight: 700, color: theme.color.heading }}>{live.percent}%</span>
        <span style={{ ...text.meta, color: theme.color.subtle }}>{live.filled} of {live.required} required</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {sections.map((s) => {
          const req = s.fields.filter((f) => f.required);
          const done = req.filter((f) => answered(payload[f.key])).length;
          return (
            <div key={s.label}>
              <div style={{ ...text.label, marginBottom: 4 }}>
                {s.label} <span style={{ fontWeight: 400 }}>({done}/{req.length})</span>
              </div>
              {s.fields.map((f) => {
                const ok = answered(payload[f.key]);
                return (
                  <div key={f.key} style={{ ...text.hint, display: "flex", alignItems: "center", gap: 6, color: ok ? theme.color.text : f.required ? theme.color.warningText : theme.color.faint, marginBottom: 2 }}>
                    {ok ? <CheckCircle2 size={12} color={theme.color.success} /> : <Circle size={12} />}
                    <span>{f.label}{f.required ? "" : " (optional)"}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}
